import { useTranslation } from "react-i18next";
import { Check, Loader2, Search } from "lucide-react";
import { Input } from "~/atoms/input";
import { cn } from "~/lib/utils";
import NoResult from "~/molecules/NoResult";
import { useDebounce } from "~/api/utils";

interface SearchableItemSelectorProps<T extends { id: string }> {
  value: string | null;
  onChange: (id: string) => void;
  items: T[] | undefined;
  isLoading: boolean;
  onSearchChange?: (search: string) => void;
  renderItem: (item: T) => { primary: string; secondary?: string };
}

export function SearchableItemSelector<T extends { id: string }>({
  value,
  onChange,
  items,
  isLoading,
  onSearchChange,
  renderItem,
}: SearchableItemSelectorProps<T>) {
  const { t } = useTranslation();

  const debouncedSearch = useDebounce((value: string) => {
    onSearchChange?.(value);
  }, 300);

  return (
    <div className="space-y-2">
      {onSearchChange && (
        <div className="relative">
          <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input
            type="text"
            placeholder={t("commons.search")}
            onChange={(e) => debouncedSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      )}

      <div className="max-h-75 overflow-y-auto rounded-md border">
        {items?.map((item) => {
          const rendered = renderItem(item);
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onChange(item.id)}
              className={cn(
                "flex w-full items-center justify-between gap-2 border-b px-3 py-2 text-left transition-colors last:border-b-0",
                {
                  "bg-primary/10": value === item.id,
                  "hover:bg-accent": value !== item.id,
                },
              )}
            >
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{rendered.primary}</div>
                {rendered.secondary && (
                  <div className="text-muted-foreground truncate text-xs">
                    {rendered.secondary}
                  </div>
                )}
              </div>
              {value === item.id && <Check className="text-primary size-6" />}
            </button>
          );
        })}

        {isLoading ? (
          <div className="py-4 text-center">
            <Loader2 className="text-muted-foreground mx-auto h-4 w-4 animate-spin" />
          </div>
        ) : items?.length === 0 ? (
          <NoResult
            message={t("sharepoint.steps.selectDestination.noSitesFound")}
          />
        ) : null}
      </div>
    </div>
  );
}
